/**
 * Skirmish AI.
 *
 * Deliberately limited to the same command interface a human has: it cannot
 * read hidden state or bypass the rules, it just issues commands into the same
 * lockstep pipeline.
 *
 * It runs only in local skirmish. In a networked match every player's commands
 * must be reproducible on every client, and this AI uses `Math.random` and
 * wall-clock pacing, so it is not safe to run remotely. Moving it into the Rust
 * simulation is what would make AI opponents work in multiplayer.
 */

import { CommandBuffer } from './commands.js';
import {
  F_ID,
  F_KIND,
  F_OWNER,
  F_X,
  F_Y,
  KIND_BARRACKS,
  KIND_HARVESTER,
  KIND_HQ,
  KIND_INFANTRY,
  KIND_REFINERY,
  KIND_TANK,
  KIND_WAR_FACTORY,
  TICKS_PER_SECOND,
} from './constants.js';

interface SimView {
  entity_count(): number;
  entity_ptr(): number;
  credits(player: number): number;
  pending_structure(player: number): number;
  can_place(player: number, kind: number, tx: number, ty: number): boolean;
}

export interface AiOptions {
  /** Seconds between AI decisions. Slower means an easier opponent. */
  thinkInterval: number;
  /** Army size the AI gathers before attacking. */
  attackThreshold: number;
}

export const AI_PRESETS: Record<string, AiOptions> = {
  easy: { thinkInterval: 3.0, attackThreshold: 14 },
  normal: { thinkInterval: 1.5, attackThreshold: 9 },
  hard: { thinkInterval: 0.8, attackThreshold: 6 },
};

export class SkirmishAi {
  private lastThinkTurn = 0;
  private buildIndex = 0;
  private attacking = false;

  /** Structures the AI works through in order, once it can afford each. */
  private readonly buildOrder = [
    KIND_REFINERY,
    KIND_BARRACKS,
    KIND_WAR_FACTORY,
    KIND_REFINERY,
    KIND_BARRACKS,
  ];

  constructor(
    private player: number,
    private opts: AiOptions,
  ) {}

  /**
   * Produces this AI player's commands for a turn. Returns an empty buffer on
   * turns where it has nothing to say, which is most of them.
   */
  commands(
    turn: number,
    sim: SimView,
    memory: WebAssembly.Memory,
    stride: number,
  ): Uint8Array {
    const interval = Math.max(1, Math.round(this.opts.thinkInterval * TICKS_PER_SECOND));
    if (turn - this.lastThinkTurn < interval) return new Uint8Array(0);
    this.lastThinkTurn = turn;

    const count = sim.entity_count();
    const view = new Float32Array(memory.buffer, sim.entity_ptr(), count * stride);
    const buf = new CommandBuffer();

    // Survey the board.
    const mine: number[] = [];
    const army: number[] = [];
    let harvesters = 0;
    let barracks = 0;
    let warFactory = 0;
    let refineries = 0;
    let hqX = 0;
    let hqY = 0;
    let enemyHqX = -1;
    let enemyHqY = -1;
    let enemyTarget = -1;
    let enemyTargetX = 0;
    let enemyTargetY = 0;

    for (let i = 0; i < count; i++) {
      const o = i * stride;
      const owner = view[o + F_OWNER] | 0;
      const kind = view[o + F_KIND] | 0;
      const id = view[o + F_ID];
      const x = view[o + F_X];
      const y = view[o + F_Y];

      if (owner === this.player) {
        mine.push(id);
        if (kind === KIND_INFANTRY || kind === KIND_TANK) army.push(id);
        if (kind === KIND_HARVESTER) harvesters++;
        if (kind === KIND_BARRACKS) barracks++;
        if (kind === KIND_WAR_FACTORY) warFactory++;
        if (kind === KIND_REFINERY) refineries++;
        if (kind === KIND_HQ) {
          hqX = x;
          hqY = y;
        }
      } else {
        if (kind === KIND_HQ) {
          enemyHqX = x;
          enemyHqY = y;
        }
        if (enemyTarget < 0) {
          enemyTarget = id;
          enemyTargetX = x;
          enemyTargetY = y;
        }
      }
    }

    const credits = sim.credits(this.player);

    // Place a finished structure before queuing anything else, since the
    // structure queue is blocked until it lands.
    const pending = sim.pending_structure(this.player);
    if (pending !== 255) {
      const spot = this.findSpot(sim, pending, hqX, hqY);
      if (spot) buf.place(this.player, pending, spot.x, spot.y);
      return buf.flush();
    }

    // Economy first: harvesters pay for everything else.
    if (warFactory > 0 && harvesters < 2 + refineries && credits > 1500) {
      buf.produce(this.player, KIND_HARVESTER);
      return buf.flush();
    }

    // Work through the build order.
    if (this.buildIndex < this.buildOrder.length) {
      const next = this.buildOrder[this.buildIndex];
      const costs: Record<number, number> = {
        [KIND_REFINERY]: 2000,
        [KIND_BARRACKS]: 500,
        [KIND_WAR_FACTORY]: 2000,
      };
      if (credits >= costs[next] + 300) {
        buf.produce(this.player, next);
        this.buildIndex++;
        return buf.flush();
      }
    }

    // Army production, biased toward tanks once a factory exists.
    if (warFactory > 0 && credits > 900 && Math.random() < 0.6) {
      buf.produce(this.player, KIND_TANK);
    } else if (barracks > 0 && credits > 200) {
      buf.produce(this.player, KIND_INFANTRY);
    }

    // Commit to an attack once the army is big enough, then keep pressing.
    if (!this.attacking && army.length >= this.opts.attackThreshold) {
      this.attacking = true;
    }
    if (this.attacking && army.length > 0) {
      if (army.length < Math.max(2, this.opts.attackThreshold / 3)) {
        // Wiped out: pull back and rebuild.
        this.attacking = false;
        buf.move(this.player, army, hqX, hqY + 3, false);
      } else if (enemyHqX >= 0) {
        buf.move(this.player, army, enemyHqX, enemyHqY, true);
      } else if (enemyTarget >= 0) {
        buf.move(this.player, army, enemyTargetX, enemyTargetY, true);
      }
    }

    return buf.flush();
  }

  /** Spirals out from the base for a legal structure site. */
  private findSpot(
    sim: SimView,
    kind: number,
    hqX: number,
    hqY: number,
  ): { x: number; y: number } | null {
    const cx = Math.round(hqX);
    const cy = Math.round(hqY);
    for (let r = 2; r <= 10; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
          const x = cx + dx;
          const y = cy + dy;
          if (sim.can_place(this.player, kind, x, y)) return { x, y };
        }
      }
    }
    return null;
  }
}
